/**
 * A centred divider-with-label that opens into the thread: the shape for things that
 * happened around a turn rather than things that were said.
 */

import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Reveal } from './reveal';

interface ThreadDisclosureProps {
  icon: LucideIcon;
  /** Extra classes for the pill's icon, such as a spinner while something runs. */
  iconClassName?: string;
  label: string;
  /** The second half of the pill (a count, a bucket, a duration), unstyled so a caller can colour it. */
  meta?: ReactNode;
  open: boolean;
  onToggle: () => void;
  /** Draws attention while something is still happening. */
  active?: boolean;
  /** Constrains the revealed panel; by default it fills the column the pill sits across. */
  contentClassName?: string;
  children: ReactNode;
}

export const ThreadDisclosure = ({
  icon: Icon,
  iconClassName,
  label,
  meta,
  open,
  onToggle,
  active = false,
  contentClassName,
  children,
}: ThreadDisclosureProps) => (
  <div className='flex w-full flex-col items-center py-1'>
    <button type='button' onClick={onToggle} className='group flex w-full max-w-[480px] items-center gap-3'>
      <span className='h-px flex-1 bg-linear-to-r from-transparent to-border' />
      <span
        className={cn(
          'flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] transition-colors',
          active
            ? 'border-indigo-200 bg-indigo-50/70 text-indigo-700'
            : 'border-border bg-muted/70 text-muted-foreground group-hover:bg-muted'
        )}
      >
        <Icon className={cn('size-3', active && 'text-indigo-500', iconClassName)} aria-hidden />
        <span className='font-medium'>{label}</span>
        {meta && (
          <span>
            <span className='opacity-70'>· </span>
            {meta}
          </span>
        )}
        <ChevronDown className={cn('size-3.5 transition-transform duration-300', open && 'rotate-180')} />
      </span>
      <span className='h-px flex-1 bg-linear-to-l from-transparent to-border' />
    </button>

    <Reveal open={open} className={cn('w-full', contentClassName)}>
      {children}
    </Reveal>
  </div>
);
